import { startNavigationFeedback, stopNavigationFeedback } from '@ui/NavigationFeedback'
import { useClinicalNoteLeaveGuardStore } from '@zustore/store'
import { useCallback, useEffect, useRef } from 'react'

type LeaveAction = () => void

// The note editor saves silently on leave: leave on success, stay on failure. The failure handler gets
// `leaveAnyway` so the screen can offer "Leave anyway" (a save that keeps failing must not trap).
type GuardConfig = {
  enabled: boolean
  flush: () => Promise<boolean>
  onFlushFailed?: (leaveAnyway: () => void) => void
}

const leaveAndNavigate = (onLeave: LeaveAction) => {
  startNavigationFeedback()
  onLeave()
}

export const useClinicalNoteLeaveGuard = ({ enabled, flush, onFlushFailed }: GuardConfig) => {
  const isLeavingByHistoryRef = useRef(false)
  const isRestoringHistoryRef = useRef(false)

  const confirmLeave = useCallback(
    (onLeave: LeaveAction) => {
      if (!enabled) {
        onLeave()
        return true
      }
      // Sidebar/back may have already started route-loading feedback; hide it while saving.
      stopNavigationFeedback()
      void flush().then(ok => {
        if (ok) leaveAndNavigate(onLeave)
        else onFlushFailed?.(() => leaveAndNavigate(onLeave))
      })
      return false
    },
    [enabled, flush, onFlushFailed]
  )

  const confirmLeaveRef = useRef(confirmLeave)

  useEffect(() => {
    confirmLeaveRef.current = confirmLeave
  }, [confirmLeave])

  useEffect(() => {
    useClinicalNoteLeaveGuardStore.getState().registerGuard(confirmLeave)
    return () => {
      useClinicalNoteLeaveGuardStore.getState().registerGuard(null)
    }
  }, [confirmLeave])

  useEffect(() => {
    if (!enabled) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [enabled])

  useEffect(() => {
    if (!enabled) return

    const handlePopState = () => {
      if (isLeavingByHistoryRef.current) return

      if (isRestoringHistoryRef.current) {
        isRestoringHistoryRef.current = false
        stopNavigationFeedback()
        return
      }

      isRestoringHistoryRef.current = true
      stopNavigationFeedback()
      window.history.forward()

      window.setTimeout(() => {
        confirmLeaveRef.current(() => {
          isLeavingByHistoryRef.current = true
          window.history.back()
        })
      }, 0)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [enabled])

  return { confirmLeave }
}
